import { memo, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useMap } from "@vis.gl/react-google-maps";
import { MapRouteProps } from "./map";

interface PolylineProps {
    route: MapRouteProps;
    onClick?: (dataId: string) => void;
    onHover?: (dataId: string | null) => void;
}

export const Polyline = memo(({ route, onClick, onHover }: PolylineProps) => {
    const theme = useTheme();
    const map = useMap();

    const { coords, color, highlight, animated } = route;
    const polylineRef = useRef<google.maps.Polyline | null>(null);
    const animationRef = useRef<number | null>(null);

    useEffect(() => {
        if (!map) return;

        const strokeColor =
            route.strokeColor ??
            (highlight
                ? theme.palette.error.main
                : (color ?? theme.palette.primary.main));

        const strokeWeight = route.strokeWeight ?? (highlight ? 5 : 3);

        const dashIcons: google.maps.IconSequence[] = animated
            ? [
                {
                    icon: {
                        path: "M 0,-1 0,1",
                        strokeColor,
                        strokeOpacity: 1,
                        strokeWeight: strokeWeight,
                        scale: strokeWeight,
                    },
                    offset: "0%",
                    repeat: `${strokeWeight * 6}px`,
                },
            ]
            : [];

        polylineRef.current = new google.maps.Polyline({
            path: coords,
            strokeColor,
            strokeWeight: animated ? 0 : strokeWeight,
            strokeOpacity: animated ? 0 : (route.strokeOpacity ?? null),
            zIndex: route.zIndex ?? null,
            icons: dashIcons,
        });

        polylineRef.current?.setMap(map);

        if (animated) {
            let count = 0;
            animationRef.current = window.setInterval(() => {
                count = (count + 1) % 200;
                const icons = polylineRef.current?.get("icons");
                if (icons?.length) {
                    icons[0].offset = count / 2 + "%";
                    polylineRef.current?.set("icons", icons);
                }
            }, 500);
        }

        if (onClick) {
            polylineRef.current.addListener("click", () => {
                onClick(route.dataId);
            });
        }

        if (onHover) {
            polylineRef.current.addListener("mouseover", () => {
                onHover(route.dataId);
            });
            polylineRef.current.addListener("mouseout", () => {
                onHover(null);
            });
        }

        return () => {
            if (animationRef.current !== null) {
                clearInterval(animationRef.current);
                animationRef.current = null;
            }
            polylineRef.current?.setMap(null);
        };
    }, [
        map,
        coords,
        color,
        highlight,
        animated,
        route.strokeColor,
        route.strokeWeight,
        route.strokeOpacity,
        route.zIndex,
        route.dataId,
        onClick,
        onHover,
    ]);

    return null;
});

Polyline.displayName = "Polyline";
